"use client"

import React, { useEffect, useState } from 'react';
import { Check, Target, TrendingUp, Sparkles, Crown, Star, CornerDownRight } from 'lucide-react';
import { getTypesPlans } from '../../services/membership/MembershipAxios';
import Loader from '../common/Loader';
import { postCreateSubscription } from '../../services/payment/payment';
import { useMetaPixel } from '../../hooks/useMetaPixel';

const PlansPanel: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const { trackEvent } = useMetaPixel();
  
  useEffect(() => {
    getTypesPlans()
      .then((res) => {
        setPlans(res);
      }).catch((err) => {
        console.error(err);
      });

  }, []);

  const handleCreateSubscription = (planId: number) => {
    const data = {
      membership_id: planId,
    }
    postCreateSubscription(data)
      .then((res) => {
        trackEvent("InitiateCheckout", { value: res.total_value_pending, currency: "USD" });
        window.location.href = res.extra_info.url;
      }).catch((err) => {
        console.error(err);
      });
  };

  const planStyles: Record<number, { color: string; borderColor: string; bgColor: string; textColor: string; Icon: any }> = {
    1: { color: 'from-blue-400 to-blue-500', borderColor: 'border-blue-200', bgColor: 'bg-blue-50', textColor: 'text-blue-600', Icon: Target },
    2: { color: 'from-indigo-400 to-indigo-500', borderColor: 'border-indigo-200', bgColor: 'bg-indigo-50', textColor: 'text-indigo-600', Icon: TrendingUp },
    3: { color: 'from-[#A854F7] to-[#A854F7]', borderColor: 'border-[#A854F7]', bgColor: 'bg-[#A854F7]', textColor: 'text-white', Icon: Sparkles },
    4: { color: 'from-violet-400 to-violet-500', borderColor: 'border-violet-200', bgColor: 'bg-violet-50', textColor: 'text-violet-600', Icon: Crown },
  };

  if (plans.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    )
  }

  const sortedByPrice = [...plans].sort((a, b) => b.price - a.price);
  const mostPopularPlanId = sortedByPrice[1]?.id;

  const processDescription = (description: string) => {
    if (!description) return null;
    const lines = description.split("/n").filter((line) => line.trim() !== "")

    return lines.map((line, index) => (
      <div key={index} className="flex items-start gap-2 mb-1">
        <CornerDownRight className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
        <span>{line.trim()}</span>
      </div>
    ))
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Elige tu plan</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, index) => {
            const { color, borderColor, bgColor, textColor, Icon } = planStyles[index + 1];
            const isPopular = plan.id === mostPopularPlanId;

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${isPopular ? 'border-[#A854F7] ring-4 ring-[#F3E7FF]' : borderColor
                  }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#A854F7] text-white px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                      <Star className="w-3 h-3" />
                      <span>Más Popular</span>
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8">
                    <div className={`w-12 h-12 mx-auto mb-4 rounded-2xl ${bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${textColor}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">{plan.benefit_plan.name}</h3>

                    <div className="mb-4">
                      <div className="flex items-baseline justify-center space-x-1">
                        <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-500">/mes</span>
                      </div>
                    </div>

                    <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${bgColor}`}>
                      <span className={`font-bold ${textColor}`}>
                        {plan.benefit_plan.credits} Créditos/mes
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <h4 className="font-semibold text-gray-900 text-center mb-4">
                      Todo incluido:
                    </h4>
                    {plan.benefit_plan.features.map((feature: any, index: number) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <div className="text-gray-700 text-sm font-medium">
                          {feature.title}
                          <div className="text-gray-500 text-xs mt-1">{processDescription(feature.description)}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    className={`w-full py-4 px-6 rounded-2xl font-bold text-white transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl bg-gradient-to-r ${color}`}
                    onClick={() => handleCreateSubscription(plan.id)}
                  >
                    Comenzar ahora
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlansPanel;
